import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BaseComponent } from "../../../../../shared/base-component";
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import {
  TrialNotificationTemplate
} from "../../../../../core/models/trial-notification-template/trial-notification-template.model";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Cultures } from "../../../../../core/constants/cultures";
import { TrialNotificationTemplateService } from "../../../../../core/services/trial-notification-template.service";
import { ButtonComponent } from "../../../../../shared/button/button.component";
import { SharedModule } from "../../../../../shared/shared.module";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { RRule } from "rrule";
import { Translation } from "../../../../../core/models/translation.model";
import {
  AddTrialNotificationTemplate
} from "../../../../../core/models/trial-notification-template/add-trial-notification-template.model";
import {
  BasicTrialNotificationTemplate
} from "../../../../../core/models/trial-notification-template/basic-trial-notification-template.model";
import {
  UpdateTrialNotificationTemplate
} from "../../../../../core/models/trial-notification-template/update-trial-notification-template.model";
import { LogHelper } from "../../../../../core/helpers/log.helper";
import { NotificationType } from "../../../../../core/constants/notification-type.enum";
import { NgForOf } from "@angular/common";
import { DiaryFormService } from "../../../../../core/services/diary-form.service";
import { DiaryForm } from "../../../../../core/models/trial-diary-form/diary-form.model";
import {
  TrialNotificationConditionsComponent
} from "../trial-notification-conditions/trial-notification-conditions.component";
import { NotificationConditions } from "../trial-notification-conditions/notification-conditions.model";
import { NotificationTrigger } from "../../../../../core/constants/notification-trigger.enum";
import { requiredNoWhitespaceValidator } from "../../../../../core/validators/no-whitespace.validator";
import { DiaryType } from "../../../../../core/constants/diary-form-type.enum";

@Component({
  selector: 'app-trial-notification-form',
  standalone: true,
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    SharedModule,
    NgForOf,
    TrialNotificationConditionsComponent
  ],
  templateUrl: './trial-notification-form.component.html',
  styleUrl: './trial-notification-form.component.scss'
})
export class TrialNotificationFormComponent extends BaseComponent implements OnInit {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;
  @ViewChild('offsetUnitSelect') offsetUnitSelect: InputSelectComponent;
  @ViewChild('afterEventSelect') afterEventSelect: InputSelectComponent;
  @ViewChild('expressionOffsetUnitSelect') expressionOffsetUnitSelect: InputSelectComponent;
  @ViewChild('conditionsManager') conditionsManager: TrialNotificationConditionsComponent;

  @Input() trialId: string;
  @Input() selectedDiaryFormId: string;

  @Output() updated = new EventEmitter<BasicTrialNotificationTemplate[]>();

  private readonly _trialNotificationTemplateService = inject(TrialNotificationTemplateService);
  private readonly _diaryFormService = inject(DiaryFormService);

  loading: boolean = false;
  notificationTemplate: TrialNotificationTemplate = new TrialNotificationTemplate();
  diaryForm: DiaryForm = new DiaryForm();
  cultureForms: FormGroup[] = [];
  availableCultures: { value: string, text: string }[] = [];
  filteredCultures: { value: string, text: string }[] = [];
  form: FormGroup = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    selectedCulture: new FormControl<string>('en'),
    trigger: new FormControl<string>('0'),
    ediaryMissedEntryCount: new FormControl<number>(0),
    notificationType: new FormControl<string>('0', [Validators.required]),
    frequency: new FormControl<string>('weekly', [Validators.required]),
    dayOfWeek: new FormControl<string>('mon', [Validators.required]),
    dayOfMonth: new FormControl<string>('1', [Validators.required]),
    time: new FormControl<string>('08:00', [Validators.required]),
    appNotificationEnabled: new FormControl<boolean>(false),
    emailNotificationEnabled: new FormControl<boolean>(false),
    mandatoryNotification: new FormControl<boolean>(false),
    rule: new FormControl<string>('', Validators.required),
    offsetUnit: new FormControl<string>('0'),
    offsetAmount: new FormControl<number>(0),
    afterEvent: new FormControl<string>('0'),
    expressionOffsetUnit: new FormControl<string>('0'),
    expressionOffsetAmount: new FormControl<number>(0),
    expression: new FormControl<string>(''),
    logicalOperator: new FormControl<string>('0')
  });
  frequencyOptions: SelectOption[] = [
    new SelectOption('weekly', 'Weekly'),
    new SelectOption('monthly', 'Monthly')
  ];
  dayOfMonthOptions: SelectOption[] = Array.from({length: 31}, (_, i) =>
    new SelectOption(String(i + 1), String(i + 1))
  );
  dayOfWeekOptions: SelectOption[] = [
    new SelectOption('mon', 'Monday'),
    new SelectOption('tue', 'Tuesday'),
    new SelectOption('wed', 'Wednesday'),
    new SelectOption('thu', 'Thursday'),
    new SelectOption('fri', 'Friday'),
    new SelectOption('sat', 'Saturday'),
    new SelectOption('sun', 'Sunday')
  ];
  offsetUnitOptions: SelectOption[] = [
    new SelectOption('0', 'Minute(s)'),
    new SelectOption('1', 'Hour(s)'),
    new SelectOption('2', 'Day(s)'),
    new SelectOption('3', 'Week(s)')
  ];
  afterEventOptions: SelectOption[] = [
    new SelectOption('0', 'Before Visit'),
    //new SelectOption('1', 'After Visit')
  ];
  triggerOptions: SelectOption[] = [
    new SelectOption('1', 'Frequency'),
    new SelectOption('2', 'Missed Entries'),
    new SelectOption('3', 'Diary Submitted'),
  ];
  protected tokens: { key: string, description: string }[] = [
    { key: 'visit_title', description: 'Visit Title' },
    { key: 'visit_date', description: 'Visit Date' },
    { key: 'visit_time', description: 'Visit Time' },
    { key: 'site_name', description: 'Site Name' },
    { key: 'site_address', description: 'Site Address' },
  ];

  protected readonly NotificationType = NotificationType;

  constructor() {
    super();

    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;
  }

  ngOnInit() {
    this.form.valueChanges.subscribe(changes => {
      this.updateNotificationValidation();
      this.updateRecurringRule();
      this.setValuesOnNotificationTemplate();
    });

    // When offset unit changes, update the offset amount so it is within the range of the selected unit
    this.form.get('offsetUnit').valueChanges.subscribe(offset => {
      let offsetAmount = this.form.value.offsetAmount;

      // Minutes
      if (offset === '0' && offsetAmount > 59) {
        this.form.patchValue({offsetAmount: 15});
      }

      // Hours
      if (offset === '1' && offsetAmount > 23) {
        this.form.patchValue({offsetAmount: 1});
      }

      // Days
      if (offset === '2' && offsetAmount > 30) {
        this.form.patchValue({offsetAmount: 1});
      }

      // Weeks
      if (offset === '3' && offsetAmount > 52) {
        this.form.patchValue({offsetAmount: 1});
      }

      // Update validation to account for offset unit changes
      this.updateNotificationValidation();
    });

    // When offset unit changes, update the offset amount so it is within the range of the selected unit
    this.form.get('expressionOffsetUnit').valueChanges.subscribe(offset => {
      let offsetAmount = this.form.value.expressionOffsetAmount;

      // Minutes
      if (offset === '0' && offsetAmount > 59) {
        this.form.patchValue({expressionOffsetAmount: 15});
      }

      // Hours
      if (offset === '1' && offsetAmount > 23) {
        this.form.patchValue({expressionOffsetAmount: 1});
      }

      // Days
      if (offset === '2' && offsetAmount > 30) {
        this.form.patchValue({expressionOffsetAmount: 1});
      }

      // Weeks
      if (offset === '3' && offsetAmount > 52) {
        this.form.patchValue({expressionOffsetAmount: 1});
      }

      // Update validation to account for offset unit changes
      this.updateNotificationValidation();
    });
  }


  buildCultureForms() {
    this.cultureForms = [];
    this.availableCultures.forEach((culture) => {
      let translations = this.notificationTemplate.translations.filter(t => t.culture === culture.value);
      let form = new FormGroup({
        culture: new FormControl<string>(culture.value),
        name: new FormControl<string>(translations.find(t => t.key === 'notification_name')?.value ?? '', culture.value === 'en' ? requiredNoWhitespaceValidator() : null),
        emailSubject: new FormControl<string>(translations.find(t => t.key === 'email_subject')?.value ?? ''),
        emailContent: new FormControl<string>(translations.find(t => t.key === 'email_content')?.value ?? ''),
        appTitle: new FormControl<string>(translations.find(t => t.key === 'app_title')?.value ?? ''),
        appContent: new FormControl<string>(translations.find(t => t.key === 'app_content')?.value ?? ''),
      });

      form.valueChanges.subscribe(changes => {
        this.setValuesOnNotificationTemplateTranslation(changes);
      });

      this.cultureForms.push(form);
    });

    this.updateNotificationValidation();
  }

  isVisitNotification() {
    return this.form.value.notificationType === '1';
  }

  isEdiaryNotification() {
    return this.form.value.notificationType === '0';
  }

  setValuesOnNotificationTemplate() {
    this.notificationTemplate.rule = this.form.value.rule;
    this.notificationTemplate.appNotificationEnabled = this.form.value.appNotificationEnabled;
    this.notificationTemplate.emailNotificationEnabled = this.form.value.emailNotificationEnabled;
    this.notificationTemplate.mandatoryNotification = this.form.value.mandatoryNotification;
  }

  setValuesOnNotificationTemplateTranslation(changes: any) {
    if (!this.notificationTemplate.translations)
      this.notificationTemplate.translations = [];

    let name = this.notificationTemplate.translations.find(t => t.culture === changes.culture && t.key === 'notification_name');
    let appTitle = this.notificationTemplate.translations.find(t => t.culture === changes.culture && t.key === 'app_title');
    let appContent = this.notificationTemplate.translations.find(t => t.culture === changes.culture && t.key === 'app_content');
    let emailSubject = this.notificationTemplate.translations.find(t => t.culture === changes.culture && t.key === 'email_subject');
    let emailContent = this.notificationTemplate.translations.find(t => t.culture === changes.culture && t.key === 'email_content');

    if (!name) {
      this.notificationTemplate.translations.push(new Translation({ culture: changes.culture, key: 'notification_name', value: changes.name}));
    } else {
      name.value = changes.name;
    }

    if (!appTitle) {
      this.notificationTemplate.translations.push(new Translation({ culture: changes.culture, key: 'app_title', value: changes.appTitle}));
    } else {
      appTitle.value = changes.appTitle;
    }

    if (!appContent) {
      this.notificationTemplate.translations.push(new Translation({ culture: changes.culture, key: 'app_content', value: changes.appContent}));
    } else {
      appContent.value = changes.appContent;
    }

    if (!emailSubject) {
      this.notificationTemplate.translations.push(new Translation({ culture: changes.culture, key: 'email_subject', value: changes.emailSubject}));
    } else {
      emailSubject.value = changes.emailSubject;
    }

    if (!emailContent) {
      this.notificationTemplate.translations.push(new Translation({ culture: changes.culture, key: 'email_content', value: changes.emailContent}));
    } else {
      emailContent.value = changes.emailContent;
    }

    // Set the value of 'name' on the main form if the english form has been updated
    if (changes.culture === 'en')
      this.notificationTemplate.name = changes.name;
  }

  updateRecurringRule() {
    const time = this.form.value.time;
    if (!time || time === '')
      return;

    const dayMap = {
      'mon': RRule.MO,
      'tue': RRule.TU,
      'wed': RRule.WE,
      'thu': RRule.TH,
      'fri': RRule.FR,
      'sat': RRule.SA,
      'sun': RRule.SU
    };

    const frequencyMap = {
      'weekly': RRule.WEEKLY,
      'monthly': RRule.MONTHLY
    };

    const [hour, minute] = time.split(':').map(Number);
    const frequency = this.form.value.frequency;

    let ruleOptions: any = {
      freq: frequencyMap[frequency],
      byhour: [hour],
      byminute: [minute]
    };

    if (frequency === 'monthly')
    {
      ruleOptions.bymonthday = [+this.form.value.dayOfMonth || 1];
    }
    else
    {
      ruleOptions.byweekday = [dayMap[this.form.value.dayOfWeek]];
    }

    const rule = new RRule(ruleOptions);

    const ruleString = rule.toString().replace('RRULE:', '');
    if (this.form.value.rule !== ruleString)
      this.form.patchValue({rule: ruleString});
  };

  updateNotificationValidation() {
    // Reset validation
    const controlNames = ['offsetUnit', 'offsetAmount', 'afterEvent', 'trigger', 'ediaryMissedEntryCount', 'expressionOffsetUnit', 'expressionOffsetAmount', 'frequency', 'dayOfWeek', 'dayOfMonth', 'time', 'rule', 'expression', 'logicalOperator'];
    controlNames.forEach(name => {
      this.form.get(name)?.setValidators(null);
    });

    if (this.form.value.notificationType === '1') {
      // Visit specific fields - add validation
      this.form.get('offsetUnit').setValidators(Validators.required);

      // Minutes - 1 to 59 minutes
      if (this.form.get('offsetUnit').value === '0') {
        this.form.get('offsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(59)]);
      }

      // Hours - 1 to 23 hours
      if (this.form.get('offsetUnit').value === '1') {
        this.form.get('offsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(23)]);
      }

      // Days - 1 to 30 days
      if (this.form.get('offsetUnit').value === '2') {
        this.form.get('offsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(30)]);
      }

      // Weeks - 1 to 52 weeks
      if (this.form.get('offsetUnit').value === '3') {
        this.form.get('offsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(52)]);
      }
    } else {
      // Ediary specific fields - add validation
      const trigger = this.form.value.trigger;

      this.form.get('trigger').setValidators(Validators.required);

      // Frequency
      if (trigger === '1') {
        this.form.get('frequency').setValidators(Validators.required);
        this.form.get('time').setValidators(Validators.required);
        this.form.get('rule').setValidators(Validators.required);

        if (this.form.value.frequency === 'monthly')
        {
          this.form.get('dayOfMonth').setValidators(Validators.required);
        }
        else
        {
          this.form.get('dayOfWeek').setValidators(Validators.required);
        }
      }

      // Missed Entries
      if (trigger === '2') {
        this.form.get('ediaryMissedEntryCount').setValidators([Validators.required, Validators.min(1)]);
      }

      // Diary Response
      if (trigger === '3') {
        this.form.get('expressionOffsetUnit').setValidators(Validators.required);
        this.form.get('expression').setValidators(Validators.required);

        // Minutes - 1 to 59 minutes
        if (this.form.get('expressionOffsetUnit').value === '0') {
          this.form.get('expressionOffsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(59)]);
        }

        // Hours - 1 to 23 hours
        if (this.form.get('expressionOffsetUnit').value === '1') {
          this.form.get('expressionOffsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(23)]);
        }

        // Days - 1 to 30 days
        if (this.form.get('expressionOffsetUnit').value === '2') {
          this.form.get('expressionOffsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(30)]);
        }

        // Weeks - 1 to 52 weeks
        if (this.form.get('expressionOffsetUnit').value === '3') {
          this.form.get('expressionOffsetAmount').setValidators([Validators.required, Validators.min(1), Validators.max(52)]);
        }
      }
    }

    // Update validation for all controls
    controlNames.forEach(name => {
      this.form.get(name)?.updateValueAndValidity({emitEvent: false});
    })


    // Culture based validation
    let englishForm = this.cultureForms.find(f => f.value.culture === 'en');
    if (!englishForm)
      return;

    // If mandatory notification, email is required
    if (!this.form.value.emailNotificationEnabled && this.form.value.mandatoryNotification) {
      this.form.patchValue({emailNotificationEnabled: true});
    }

    // Set validation of app notifications
    if (this.form.value.appNotificationEnabled) {
      englishForm.get('appTitle').setValidators(requiredNoWhitespaceValidator());
      englishForm.get('appContent').setValidators(requiredNoWhitespaceValidator());
    } else {
      englishForm.get('appTitle').setValidators(null);
      englishForm.get('appContent').setValidators(null);
    }

    englishForm.get('appTitle').updateValueAndValidity();
    englishForm.get('appContent').updateValueAndValidity();

    // Set validation of email notifications
    if (this.form.value.emailNotificationEnabled) {
      englishForm.get('emailSubject').setValidators(requiredNoWhitespaceValidator());
      englishForm.get('emailContent').setValidators(requiredNoWhitespaceValidator());
    } else {
      englishForm.get('emailSubject').setValidators(null);
      englishForm.get('emailContent').setValidators(null);
    }

    englishForm.get('emailSubject').updateValueAndValidity();
    englishForm.get('emailContent').updateValueAndValidity();
  }

  add(type: NotificationType = NotificationType.Ediary, diaryFormId?: string) {
    this.form.reset();
    this.form.patchValue({
      isBusy: false,
      notificationType: type.toString(),
      appNotificationEnabled: false,
      emailNotificationEnabled: false,
      mandatoryNotification: false,
      offsetUnit: '0',
      offsetAmount: 15,
      afterEvent: '0',
      trigger: type === NotificationType.Ediary ? '1' : '0',
      ediaryMissedEntryCount: 0,
      expressionOffsetUnit: '0',
      expressionOffsetAmount: 15,
      expression: ''
    });
    this.cultureForms = [];
    this.notificationTemplate = new TrialNotificationTemplate();

    this.form.patchValue({
      isBusy: false,
      selectedCulture: 'en',
      appNotificationEnabled: false,
      emailNotificationEnabled: false,
    });

    if (diaryFormId)
      this.loadDiaryForm(diaryFormId);

    this.buildCultureForms();
    this.form.markAsUntouched();

    this.modal.show();
  }

  selectedTriggerLabel(): string {
    let trigger = this.triggerOptions.find(t => t.value === this.form.value.trigger);
    return trigger?.text ?? '';
  }

  parseRRule(ruleString: string): { frequency: string; dayOfWeek: string; dayOfMonth: string; time: string } {
    if (!ruleString)
    {
      return ({ frequency: 'weekly', dayOfWeek: 'mon', dayOfMonth: '1', time: '08:00' });
    }

    const rule = RRule.fromString(ruleString);
    const options = rule.options;

    // Map frequency to this.frequencyOptions
    const frequencyMap = {
      [RRule.WEEKLY]: 'weekly',
      [RRule.MONTHLY]: 'monthly'
    };
    const frequency = frequencyMap[options.freq] || 'weekly';

    // Map day of week to this.dayOfWeekOptions
    const dayMap = {
      [RRule.MO.weekday]: 'mon',
      [RRule.TU.weekday]: 'tue',
      [RRule.WE.weekday]: 'wed',
      [RRule.TH.weekday]: 'thu',
      [RRule.FR.weekday]: 'fri',
      [RRule.SA.weekday]: 'sat',
      [RRule.SU.weekday]: 'sun'
    };
    const dayOfWeek = options.byweekday && options.byweekday.length > 0
      ? dayMap[(options.byweekday[0] as any).weekday ?? options.byweekday[0]]
      : 'mon';

    const dayOfMonth = options.bymonthday && options.bymonthday.length > 0
      ? String(options.bymonthday[0])
      : '1';

    // Convert hours/minutes into a time format 08:00
    const hour = options.byhour && options.byhour.length > 0 ? options.byhour[0] : 8;
    const minute = options.byminute && options.byminute.length > 0 ? options.byminute[0] : 0;
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

    return { frequency, dayOfWeek, dayOfMonth, time };
  }

  update(templateId: string) {
    this.form.reset();
    this.cultureForms = [];
    this.modal.show();

    this.loading = true;
    this._trialNotificationTemplateService.getTemplate(templateId).subscribe({
      next: template => {
        this.notificationTemplate = template;

        if (template.diaryFormId)
          this.loadDiaryForm(template.diaryFormId);

        const { frequency, dayOfWeek, dayOfMonth, time } = this.parseRRule(template.rule);

        this.form.patchValue({
          selectedCulture: 'en',
          frequency,
          dayOfWeek,
          dayOfMonth,
          time,
          appNotificationEnabled: template.appNotificationEnabled,
          emailNotificationEnabled: template.emailNotificationEnabled,
          mandatoryNotification: template.mandatoryNotification,
          rule: template.rule,
          notificationType: String(template.notificationType),
          offsetUnit: String(template.offsetUnit),
          offsetAmount: template.offsetAmount,
          afterEvent: template.afterEvent === true ? '1' : '0',
          trigger: template.trigger ? String(template.trigger) : '0',
          ediaryMissedEntryCount: template.ediaryMissedEntryCount,
          expression: template.expression,
          logicalOperator: template.logicalOperator,
          expressionOffsetUnit: String(template.offsetUnit),
          expressionOffsetAmount: template.offsetAmount
        });

        // Initial conditions if this is an ediary response notification (delay to give time for component to render)
        if (template.notificationType === NotificationType.Ediary && template.trigger === NotificationTrigger.DiaryResponse) {
          setTimeout(() => {
            this.conditionsManager.init(new NotificationConditions({
              logicalOperator: template.logicalOperator,
              expressions: JSON.parse(template.expression)
            }));
          }, 500);
        }

        this.buildCultureForms();
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  cultureFormsValid() {
    return this.cultureForms.every(cultureForm => cultureForm.valid);
  }

  onConditionsChanged(conditions: NotificationConditions) {
    this.form.patchValue({
      logicalOperator: String(conditions.logicalOperator),
      expression: conditions.expressions.length > 0 ? JSON.stringify(conditions.expressions) : null
    });
  }

  isDiaryResponseTrigger() {
    return this.isEdiaryNotification() && this.form.value.trigger === '3';
  }

  private updateTriggerOptions() {
    const isPeriodic = this.diaryForm?.diaryType === DiaryType.Periodic;
    this.triggerOptions = [
      new SelectOption('1', 'Frequency'),
      ...(isPeriodic ? [new SelectOption('2', 'Missed Entries')] : []),
      new SelectOption('3', 'Diary Submitted'),
    ];
  }

  private loadDiaryForm(diaryFormId?: string) {
    if (diaryFormId) {
      this._diaryFormService.getById(diaryFormId).subscribe(diaryForm => {
        this.diaryForm = diaryForm;
        this.updateTriggerOptions();
      });
    } else {
      this._diaryFormService.list(this.trialId).subscribe(summaries => {
        if (summaries.length > 0) {
          this._diaryFormService.getById(summaries[0].id).subscribe(diaryForm => {
            this.diaryForm = diaryForm;
            this.updateTriggerOptions();
          });
        }
      });
    }
  }

  onSubmit() {
    // Check main form and culture forms are valid
    if (this.form.invalid || !this.cultureFormsValid())
      return;

    this.form.patchValue({isBusy: true});

    const offsetUnitValue = this.form.value.offsetUnit;
    const parsedOffsetUnit = (!offsetUnitValue || offsetUnitValue === '' || offsetUnitValue === 'null') ? null : +offsetUnitValue;

    const expressionOffsetUnitValue = this.form.value.expressionOffsetUnit;
    const parsedExpressionOffsetUnit = (!expressionOffsetUnitValue || expressionOffsetUnitValue === '' || expressionOffsetUnitValue === 'null') ? null : +expressionOffsetUnitValue;

    if (this.notificationTemplate.id && this.notificationTemplate.id !== '') {
      // Update
      let dto = new UpdateTrialNotificationTemplate({
        id: this.notificationTemplate.id,
        trialId: this.trialId,
        rule: this.notificationTemplate.rule,
        appNotificationEnabled: this.notificationTemplate.appNotificationEnabled,
        emailNotificationEnabled: this.notificationTemplate.emailNotificationEnabled,
        mandatoryNotification: this.notificationTemplate.mandatoryNotification,
        translations: this.notificationTemplate.translations,
        offsetUnit: this.isDiaryResponseTrigger() ? parsedExpressionOffsetUnit : parsedOffsetUnit,
        offsetAmount: this.isDiaryResponseTrigger() ? this.form.value.expressionOffsetAmount : this.form.value.offsetAmount,
        afterEvent: this.form.value.afterEvent === '1',
        trigger: +this.form.value.trigger,
        ediaryMissedEntryCount: this.form.value.ediaryMissedEntryCount,
        expression: this.form.value.expression,
        logicalOperator: +this.form.value.logicalOperator
      });

      console.log(dto);

      this._trialNotificationTemplateService.update(dto).subscribe({
        next: templates => {
          this.form.patchValue({isBusy: false});
          this.updated.emit(templates);
          this.modal.hide();
        },
        error: err => {
          LogHelper.log(err);
          this._alertService.showErrorAlert(err);
          this.form.patchValue({isBusy: false});
        }
      });
    } else {
      // Create
      let dto = new AddTrialNotificationTemplate({
        trialId: this.trialId,
        diaryFormId: this.diaryForm?.id,
        notificationType: +this.form.value.notificationType,
        rule: this.notificationTemplate.rule,
        appNotificationEnabled: this.notificationTemplate.appNotificationEnabled,
        emailNotificationEnabled: this.notificationTemplate.emailNotificationEnabled,
        mandatoryNotification: this.notificationTemplate.mandatoryNotification,
        translations: this.notificationTemplate.translations,
        offsetUnit: this.isDiaryResponseTrigger() ? parsedExpressionOffsetUnit : parsedOffsetUnit,
        offsetAmount: this.isDiaryResponseTrigger() ? this.form.value.expressionOffsetAmount : this.form.value.offsetAmount,
        afterEvent: this.form.value.afterEvent === '1',
        trigger: +this.form.value.trigger,
        ediaryMissedEntryCount: this.form.value.ediaryMissedEntryCount,
        expression: this.form.value.expression,
        expressionOffsetAmount: this.form.value.expressionsOffsetAmount,
        expressionOffsetUnit: parsedExpressionOffsetUnit,
        logicalOperator: +this.form.value.logicalOperator
      });

      this._trialNotificationTemplateService.create(dto).subscribe({
        next: templates => {
          this.form.patchValue({isBusy: false});
          this.updated.emit(templates);
          this.modal.hide();
        },
        error: err => {
          LogHelper.log(err);
          this._alertService.showErrorAlert(err);
          this.form.patchValue({isBusy: false});
        }
      });
    }

  }

}
